import { TimeUnit as AggregatePeriod, EqlField } from 'generated/graphql';
import { GroupBy, TimeRange, TimeUnit } from 'v2/core/entities';

import { SortBy } from 'v2/core/entities/feedback/sortBy';
import { SourceDetail } from 'v2/core/entities/feedback/sources';
import { SourceName, SourceNameWithNamespace } from 'v2/core/entities/fields';
import { Timezone } from 'v2/core/entities/user';
import { UUID } from 'v2/core/entities/uuid';
import {
  EQLProps,
  ExpressionType,
  GroupByField,
} from 'v2/core/repository/eql/types';
import {
  OperatorContainsAnyOf,
  OperatorIsGreaterThan,
  OperatorIsLessThan,
} from 'v2/core/services/queryBuilder/operators';
import {
  FilterQueryOperation,
  FilterUnion,
  HasAspectFilter,
  HasReasonFilter,
  MetadataFilter,
  OperationType,
  WhereClause,
  entityFilter,
  l1Filter,
  reasonFilter,
} from 'v2/core/services/queryBuilder/whereClause';
import { escapeSingleDoubleQuotesAndBackSlash } from '../edge-server/utils/escapeSingleDoubleQuotesAndBackslash';
import { mapEQLValues } from './mapEQLValues';
import { mapFilterPredictedOn } from './mapFilterPredictedOn';
import { mutateEQLPropsMaybe } from './mutateEQLPropsMaybe';
import { wrapExpressionType } from './wrapExpressionType';

export const mapToEQL = (eqlProps: EQLProps): string => {
  const {
    expressionType,
    sourceDetailsMap,
    whereClause,
    dateRange,
    groupBy,
    sortBy,
    aggregatePeriod,
    timezone,
    taxonomyID,
    eqlFields,
    delta,
    functions,
  } = mutateEQLPropsMaybe(eqlProps);

  const expressionTypeWrapped = wrapExpressionType(expressionType, functions);
  const joinClause = mapToJoinClause(eqlProps.joins, sourceDetailsMap);
  const where = whereClause
    ? mapToWhereClause(whereClause, sourceDetailsMap, eqlFields)
    : '';
  const groupByClause =
    expressionType !== ExpressionType.Records
      ? mapToGroupByClause(aggregatePeriod, timezone, groupBy)
      : '';
  const sortByClause = mapToSortByClause(sortBy);
  const timeRangeClause = mapToTimeRangeClause(dateRange);
  const taxonomyClause = mapToTaxonomyClause(taxonomyID);
  const deltaClause = mapToDeltaClause(
    sourceDetailsMap,
    eqlFields,
    delta,
    eqlProps.deltaJoins,
  );

  return [
    'select',
    expressionTypeWrapped,
    joinClause,
    where,
    groupByClause,
    sortByClause,
    timeRangeClause,
    taxonomyClause,
    deltaClause,
  ]
    .filter(Boolean)
    .join(' ');
};

const mapToJoinClause = (
  joins: EQLProps['joins'],
  sourceDetailsMap: Map<SourceName, SourceDetail>,
): string | undefined => {
  if (!joins?.length) {
    return '';
  }

  return joins
    .map((join) => {
      const eql = mapToEQL({ ...join?.eql, sourceDetailsMap });

      if (!eql) {
        return undefined;
      }

      return `join (${eql}) as '${join.alias}'`;
    })
    .filter(Boolean)
    .join(' ');
};

const mapToWhereClause = (
  whereClause: WhereClause,
  sourceDetailsMap: Map<SourceName, SourceDetail>,
  eqlFields?: EqlField[],
): string | undefined => {
  const expression = mapWhereClauseToExpression(
    whereClause,
    sourceDetailsMap,
    eqlFields,
  );
  if (expression) {
    return `where ${expression}`;
  }
};

export const mapWhereClauseToExpression = (
  where: WhereClause,
  sourceDetailsMap: Map<SourceName, SourceDetail>,
  eqlFields?: EqlField[],
): string => {
  const nodeExpression = mapWhereClauseNodeToExpression(
    where,
    sourceDetailsMap,
    eqlFields,
  );
  return nodeExpression;
};

const mapWhereClauseNodeToExpression = (
  node: WhereClause,
  sourceDetailsMap: Map<SourceName, SourceDetail>,
  eqlFields?: EqlField[],
): string => {
  if (node.filter) {
    return mapFilterToExpression(
      node.filter,
      sourceDetailsMap,
      node.exclude ?? false,
      eqlFields,
    );
  }
  if (node.op && node.op.params.length > 0) {
    const opExpression = mapOperationToExpression(
      node.op,
      sourceDetailsMap,
      eqlFields,
    );
    return node.exclude && opExpression
      ? `(NOT ${opExpression})`
      : opExpression;
  }
  return '';
};

const mapMetadataValuesForContainsOperator = (filter: MetadataFilter) => {
  return filter.operator === OperatorContainsAnyOf
    ? filter.values.map((value) => `%${value}%`)
    : filter.values.map((value) => value.toString());
};

const mapMetadataValue = (filter: MetadataFilter): string => {
  const values = mapMetadataValuesForContainsOperator(filter);

  switch (filter.type) {
    case 'string':
      return mapEQLValues(values);
    case 'number':
    case 'boolean':
      return values.map((value) => value).join(', ');
  }
};

const toSourceEQLValues = (
  sources: SourceNameWithNamespace[],
  sourceDetailsMap: Map<SourceName, SourceDetail>,
): string[] => {
  return sources.map((source) => {
    const detail = sourceDetailsMap.get(source.name);

    if (!detail) {
      return source.name;
    }

    const namespace = source?.namespace;
    return namespace ? `${detail.eqlValue}-${namespace}` : detail.eqlValue;
  });
};

export const toEntityValues = (
  entities: entityFilter['entities'],
): string[] => {
  return entities.map(({ entityID, sentiment }) =>
    sentiment ? `${entityID}:${sentiment}` : entityID,
  );
};

const toSearchValues = (
  hybridSearchFilter: FilterUnion['hybridSearchFilter'],
  type: FilterUnion['type'],
): string => {
  if (!hybridSearchFilter) {
    return '';
  }
  if (hybridSearchFilter.query) {
    return `'${hybridSearchFilter.query.replace(
      /'/g, // We use single quotes to wrap the input,
      '"', // their presence in filter query will lead to invalid EQL
    )}'`;
  }
  if (!hybridSearchFilter.queries) {
    return '';
  }
  const op = type === OperationType.AND ? 'all' : 'one';
  return `${op} of (${mapEQLValues(hybridSearchFilter.queries)})`;
};

const mapFilterHavingCategories = (categoryFilterValues: string[]) => {
  return ` having categories (${mapEQLValues(categoryFilterValues)})`;
};

const mapFilterHavingL1IDs = (l1IDs: UUID[]) => {
  return ` having l1 (${mapEQLValues(l1IDs)})`;
};

const mapRelationExpressionForEntityOrReasonFilter = (
  filter: entityFilter | reasonFilter | HasAspectFilter | HasReasonFilter,
  exclude: boolean,
) => {
  if (exclude) {
    return '';
  }

  let combinedRelationExpression = '';
  if (filter.categories?.length) {
    combinedRelationExpression = mapFilterHavingCategories(filter.categories);
  }

  if ('l1IDs' in filter && filter.l1IDs?.length) {
    combinedRelationExpression = `${combinedRelationExpression}${mapFilterHavingL1IDs(
      filter.l1IDs,
    )}`;
  }

  if (combinedRelationExpression) {
    return combinedRelationExpression;
  }

  if ('predictedOn' in filter) {
    return mapFilterPredictedOn(filter.predictedOn);
  }

  return '';
};

const mapRelationExpressionForL1Filter = (
  filter: l1Filter,
  exclude: boolean,
) => {
  if (exclude) {
    return '';
  }

  if (filter.categories?.length) {
    return mapFilterHavingCategories(filter.categories);
  }
  return '';
};

const getOperator = (filter: FilterUnion, exclude?: boolean) => {
  if (filter.metadataFilter?.operator === OperatorContainsAnyOf) {
    return 'like';
  }
  if (filter.metadataFilter?.operator === OperatorIsGreaterThan) {
    return '>';
  }
  if (filter.metadataFilter?.operator === OperatorIsLessThan) {
    return '<';
  }
  if (exclude) {
    return '!=';
  }

  return '=';
};

const mapFilterToExpression = (
  filter: FilterUnion,
  sourceDetailsMap: Map<SourceName, SourceDetail>,
  exclude: boolean,
  eqlFields?: EqlField[],
): string => {
  const operator = `${getOperator(filter, exclude)} ${
    filter.type === OperationType.AND ? 'all' : 'any'
  }`;

  if (filter.entityFilter) {
    return `(entity${mapRelationExpressionForEntityOrReasonFilter(
      filter.entityFilter,
      exclude,
    )} ${operator} (${mapEQLValues(
      toEntityValues(filter.entityFilter.entities),
    )}))`;
  }

  if (filter.reasonFilter) {
    return `(reason${mapRelationExpressionForEntityOrReasonFilter(
      filter.reasonFilter,
      exclude,
    )} ${operator} (${mapEQLValues(filter.reasonFilter.reasonIDs)}))`;
  }

  if (filter.l1Filter) {
    return `(l1${mapRelationExpressionForL1Filter(
      filter.l1Filter,
      exclude,
    )} ${operator} (${mapEQLValues(filter.l1Filter.l1IDs)}))`;
  }

  if (filter.labelHierarchyFilter) {
    return `(theme ${operator} (${mapEQLValues(
      filter.labelHierarchyFilter.nodeIDs,
    )}))`;
  }

  if (filter.metadataFilter) {
    const key = escapeSingleDoubleQuotesAndBackSlash(filter.metadataFilter.key);

    return `(${key} ${operator} (${mapMetadataValue(filter.metadataFilter)}))`;
  }

  if (filter.languageFilter) {
    return `(language ${operator} (${mapEQLValues(
      filter.languageFilter.iso6393s,
    )}))`;
  }

  if (filter.idFilter) {
    return `(id ${operator} (${mapEQLValues(filter.idFilter.ids)}))`;
  }

  if (filter.sourceFilter) {
    return `(source ${operator} (${mapEQLValues(
      toSourceEQLValues(filter.sourceFilter.sources, sourceDetailsMap),
    )}))`;
  }

  if (filter.reviewSentimentFilter) {
    return filter.reviewSentimentFilter.sentimentIDs
      ? `(sentiment ${operator} (${mapEQLValues(
          filter.reviewSentimentFilter.sentimentIDs,
        )}))`
      : '';
  }

  if (filter.hybridSearchFilter) {
    const hybridSearchFilter = `(feedback${mapFilterPredictedOn(
      filter.hybridSearchFilter.predictedOn,
    )} like ${toSearchValues(filter.hybridSearchFilter, filter.type)})`;

    return exclude ? `(not ${hybridSearchFilter})` : hybridSearchFilter;
  }

  if (filter.hasAspectFilter) {
    return `(${
      exclude ? 'not' : ''
    } record has entity${mapRelationExpressionForEntityOrReasonFilter(
      filter.hasAspectFilter,
      exclude,
    )})`;
  }

  if (filter.hasReasonFilter) {
    return `(${
      exclude ? 'not' : ''
    } record has reason${mapRelationExpressionForEntityOrReasonFilter(
      filter.hasReasonFilter,
      exclude,
    )})`;
  }

  if (filter.hasMetadataFilter && filter.hasMetadataFilter.keys.length) {
    return `(${exclude ? 'not' : ''} record has any (${mapEQLValues(
      filter.hasMetadataFilter.keys,
    )}))`;
  }

  if (filter.hasUserContentFilter) {
    return `(${exclude ? 'not' : ''} record has userContent)`;
  }

  if (filter.categoryFilter) {
    return `(category ${operator} (${mapEQLValues(
      filter.categoryFilter.categories,
    )}))`;
  }

  if (filter.hasSummaryFilter) {
    return `(${exclude ? 'not' : ''} record has summary)`;
  }

  throw Error('invalid filter in where clause');
};

const mapOperationToExpression = (
  op: FilterQueryOperation,
  sourceDetailsMap: Map<SourceName, SourceDetail>,
  eqlFields?: EqlField[],
): string => {
  const expression = `${op?.params
    ?.map((param: WhereClause) =>
      mapWhereClauseToExpression(param, sourceDetailsMap, eqlFields),
    )
    .filter(Boolean)
    .join(` ${op?.type} `)}`;
  return expression ? `(${expression})` : '';
};

const toGroupByFields = (groupBy?: GroupBy): GroupByField[] => {
  const fields: GroupByField[] = [];
  if (groupBy?.source) {
    fields.push('source');
  }
  if (groupBy?.language) {
    fields.push('language');
  }
  if (groupBy?.metadata) {
    fields.push(`'${groupBy.metadata}'`);
  }
  if (groupBy?.reason) {
    fields.push('reason');
  }
  if (groupBy?.entity) {
    fields.push('entity');
  }
  if (groupBy?.entityCategory) {
    fields.push('entityCategory');
  }
  if (groupBy?.recordSentiment) {
    fields.push('sentiment');
  }
  if (groupBy?.entitySentiment) {
    fields.push('entitySentiment');
  }
  if (groupBy?.predictedOn) {
    fields.push('predictedOn');
  }
  if (groupBy?.l1) {
    fields.push('l1');
  }
  return fields;
};

const mapToGroupByClause = (
  aggregatePeriod?: AggregatePeriod,
  timezone?: Timezone,
  groupBy?: GroupBy,
): string | undefined => {
  const groupByFields = toGroupByFields(groupBy);
  const time = aggregatePeriod
    ? mapAggregatePeriodToGroupBy(aggregatePeriod, timezone)
    : '';

  const fields = [time, ...groupByFields].filter(Boolean).join(', ');

  if (fields) {
    return `group by ${fields}`;
  }
};

const mapToSortByField = (sortBy: SortBy): string => {
  if (sortBy.timestamp) {
    return 'time';
  }
  if (sortBy.source) {
    return 'source';
  }
  if (sortBy.metadataKey) {
    return `${sortBy.metadataKey}`;
  }
  throw Error('invalid sort by field');
};

const mapToSortByClause = (sortBy?: SortBy): string => {
  if (!sortBy) {
    return '';
  }
  const sortByField = mapToSortByField(sortBy);
  return `order by ${sortByField} ${sortBy.isAsc ? 'asc' : 'desc'}`;
};

export const mapToTimeUnit = (aggregatePeriod?: AggregatePeriod): TimeUnit => {
  switch (aggregatePeriod) {
    case AggregatePeriod.Day:
      return TimeUnit.Day;
    case AggregatePeriod.Week:
      return TimeUnit.Week;
    case AggregatePeriod.Month:
      return TimeUnit.Month;
    case AggregatePeriod.Quarter:
      return TimeUnit.Quarter;
    case AggregatePeriod.Year:
      return TimeUnit.Year;
    default:
      throw Error('invalid aggregation period');
  }
};

const mapAggregatePeriodToGroupBy = (
  aggregatePeriod?: AggregatePeriod,
  timezone?: Timezone,
): string => {
  if (!aggregatePeriod) {
    return '';
  }

  const unit = mapToTimeUnit(aggregatePeriod);
  const timezoneString = timezone ? `, '${timezone}'` : '';
  return `calendar(${unit}${timezoneString})`;
};

export const mapToTimeRangeClause = (dateRange?: TimeRange): string => {
  if (!dateRange) {
    return '';
  }
  switch (dateRange.type) {
    case 'absolute':
      return `timerange(${dateRange.startAfter.unix()}, ${dateRange.endBefore.unix()})`;
    case 'last':
      return `timerange(now, ${dateRange.value}${dateRange.unit})`;
    case 'since':
      return `timerange(${dateRange.startAfter.unix()}, now)`;
  }
};

const mapToTaxonomyClause = (taxonomyID?: UUID): string => {
  return taxonomyID ? `using taxonomy '${taxonomyID}'` : '';
};

const mapToDeltaClause = (
  sourceDetailsMap: Map<SourceName, SourceDetail>,
  eqlFields: EqlField[] | undefined,
  delta: EQLProps['delta'] | undefined,
  deltaJoins: EQLProps['deltaJoins'] | undefined,
): string => {
  const joins = mapToJoinClause(deltaJoins, sourceDetailsMap);

  let deltaValue = '';

  switch (delta?.type) {
    case 'deltaOffset':
      const { aggregatePeriod, multipler } = delta.value;
      deltaValue = `${multipler}${mapToTimeUnit(aggregatePeriod)}`;
      break;
    case 'whereClause':
      deltaValue = `${mapToWhereClause(
        delta.value,
        sourceDetailsMap,
        eqlFields,
      )}`;
      break;
    case 'timerange':
      deltaValue = `${mapToTimeRangeClause(delta.value)}`;
      break;
    default:
      return '';
  }

  if (joins && delta.type === 'whereClause') {
    return `delta (${joins} ${deltaValue})`;
  }

  const shouldWrapInBrackets = delta?.type !== 'deltaOffset';

  return shouldWrapInBrackets ? `delta (${deltaValue})` : `delta ${deltaValue}`;
};
